import React from 'react';
import { Link } from 'react-router-dom';
import { getImg } from './Views/socialstate';
import { formatDayLabel, formatLongDate, formatShortDate } from '../utils/formatDate';
import './Analytics.css';

// The analytics presentation itself, split out of Analytics.js so the
// admin detail view's "Complete Analytics" section renders the identical
// thing for someone else's card instead of a second implementation that
// drifts from what the customer actually sees. Takes an already-fetched
// payload; who fetched it, and from which endpoint, is the caller's
// business (GET /api/users/analytics for the customer's own page,
// GET /api/admin/users/:id/analytics for the admin one -- both are the
// same builder in utils/analytics.js).
//
// `leadsTo` makes the leads summary card a link to that route. The
// customer page passes /leads; the admin page passes nothing, since
// /leads would show the *admin's* leads, not this cardholder's.

const PLATFORM_LABEL = (platform) =>
  platform === 's_email' ? 'Email' : platform.charAt(0).toUpperCase() + platform.slice(1);

// Shared renderer for the three CSS-only bar charts (views, contacts
// saved, leads) -- same markup, different data/color/title so each one
// reads as its own chart rather than a wall of identical grey bars.
const DayChart = ({ title, series, colorClass }) => {
  const max = Math.max(1, ...series.map((d) => d.count));
  return (
    <div className="analytics-section">
      <h3 className="analytics-section__title">{title}</h3>
      <div className="analytics-barchart">
        {series.map((d) => (
          <div className="analytics-barchart__col" key={d.date}>
            <div className="analytics-barchart__track">
              <div
                className={`analytics-barchart__bar ${colorClass}`}
                style={{ height: `${(d.count / max) * 100}%` }}
                title={`${formatDayLabel(d.date)}: ${d.count}`}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="analytics-barchart__labels">
        <span>{formatDayLabel(series[0].date)}</span>
        <span>{formatDayLabel(series[series.length - 1].date)}</span>
      </div>
    </div>
  );
};

const AnalyticsView = ({ data, leadsTo, emptyMessage }) => {
  const isEmpty =
    data.totals.views === 0 &&
    data.totals.clicks === 0 &&
    data.totals.vcardSaves === 0 &&
    data.totals.leads === 0;

  const maxCountryCount = data.topCountries.length
    ? Math.max(...data.topCountries.map((c) => c.count))
    : 1;
  const maxLinkCount = data.clicksPerLink.length
    ? Math.max(...data.clicksPerLink.map((l) => l.count))
    : 1;

  if (isEmpty) {
    return (
      <div className="analytics-empty">
        <div className="analytics-empty__icon">
          <span role="img" aria-label="Bar chart">📊</span>
        </div>
        <h3>No activity yet</h3>
        <p>
          {emptyMessage ||
            "Once people start viewing your card, you'll see profile views, link clicks, contacts saved, and leads show up here -- data starts accumulating the moment someone opens your profile."}
        </p>
      </div>
    );
  }

  const leadsCard = (
    <>
      <p className="analytics-summary__value">{data.totals.leads}</p>
      <p className="analytics-summary__label">Leads</p>
      <p className="analytics-summary__tag">Since {formatShortDate(data.leadsSince)}</p>
    </>
  );

  return (
    <>
      <div className="analytics-summary">
        <div className="analytics-summary__card">
          <p className="analytics-summary__value">{data.totals.views}</p>
          <p className="analytics-summary__label">Total views</p>
          <p className="analytics-summary__tag">All-time</p>
        </div>
        <div className="analytics-summary__card">
          <p className="analytics-summary__value">{data.totals.clicks}</p>
          <p className="analytics-summary__label">Total clicks</p>
          <p className="analytics-summary__tag">All-time</p>
        </div>
        <div className="analytics-summary__card">
          <p className="analytics-summary__value">{data.totals.vcardSaves}</p>
          <p className="analytics-summary__label">Contacts saved</p>
          <p className="analytics-summary__tag">All-time</p>
        </div>
        {leadsTo ? (
          <Link to={leadsTo} className="analytics-summary__card analytics-summary__card--link">
            {leadsCard}
          </Link>
        ) : (
          <div className="analytics-summary__card">{leadsCard}</div>
        )}
      </div>

      <p className="analytics-disclaimer">
        Views, clicks, and contacts-saved totals above are all-time. The
        daily charts and country breakdown below only cover activity
        since {formatLongDate(data.eventsSince)}, so they'll show less
        than the totals -- that's expected, not a bug. Leads have no
        historical counter: the leads total and daily breakdown both
        start from {formatLongDate(data.leadsSince)}, when lead capture
        went live.
      </p>

      <DayChart
        title="Views, last 30 days"
        series={data.viewsPerDay}
        colorClass="analytics-barchart__bar--views"
      />

      <DayChart
        title="Contacts saved, last 30 days"
        series={data.vcardSavesPerDay}
        colorClass="analytics-barchart__bar--vcard"
      />

      <DayChart
        title="Leads, last 30 days"
        series={data.leadsPerDay}
        colorClass="analytics-barchart__bar--leads"
      />

      <div className="analytics-section">
        <h3 className="analytics-section__title">Top countries</h3>
        {data.topCountries.length === 0 ? (
          <p className="analytics-section__empty">No location data yet.</p>
        ) : (
          <div className="analytics-list">
            {data.topCountries.map((c) => (
              <div className="analytics-list__row" key={c.country}>
                <span className="analytics-list__label">{c.country}</span>
                <div className="analytics-list__track">
                  <div
                    className="analytics-list__bar"
                    style={{ width: `${(c.count / maxCountryCount) * 100}%` }}
                  />
                </div>
                <span className="analytics-list__count">{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="analytics-section">
        <h3 className="analytics-section__title">Clicks by link</h3>
        {data.clicksPerLink.length === 0 ? (
          <p className="analytics-section__empty">No link clicks yet.</p>
        ) : (
          <div className="analytics-links">
            {data.clicksPerLink.map((l) => (
              <div className="analytics-link-row" key={l.platform}>
                <div className="analytics-link-row__icon">{getImg(l.platform)}</div>
                <span className="analytics-link-row__label">{PLATFORM_LABEL(l.platform)}</span>
                <div className="analytics-list__track analytics-list__track--flex">
                  <div
                    className="analytics-list__bar"
                    style={{ width: `${(l.count / maxLinkCount) * 100}%` }}
                  />
                </div>
                <span className="analytics-list__count">{l.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default AnalyticsView;
